/**
 * Order of the corrective actions in the input view (JIM-1030-15, Omar and
 * Jenna at the 10/30 walkthrough: dozens of actions scattered across a site
 * are easier to work when they run by lot). The list sorts itself by lot
 * number as rows are added, cloned or given a location; the inspector can
 * still drag the order by hand, after which it stays put. The printed report
 * keeps date order (the API orders findings by when they were created).
 */

type Located = { location?: string | null };

/**
 * The lot number inside a location. "Lot 12", "12", "L-12B", "Block 3 Lot 4"
 * (→ 4: the number after "lot" wins) all count; "Main St" has none.
 */
export function lotNumber(location?: string | null): number | null {
  const text = (location ?? '').trim();
  if (!text) return null;
  const afterLot = /\blot\s*#?\s*(\d+)/i.exec(text);
  const first = /(\d+)/.exec(text);
  const hit = afterLot?.[1] ?? first?.[1];
  return hit ? Number(hit) : null;
}

/**
 * Rows with a lot number first, in ascending order (a tie keeps its order);
 * rows without one keep their order after them (street names, "entrance").
 */
export function sortByLot<T extends Located>(items: T[]): T[] {
  const keyed = items.map((item, index) => ({
    item,
    index,
    lot: lotNumber(item.location),
  }));
  keyed.sort((a, b) => {
    if (a.lot === null && b.lot === null) return a.index - b.index;
    if (a.lot === null) return 1;
    if (b.lot === null) return -1;
    return a.lot - b.lot || a.index - b.index;
  });
  return keyed.map((k) => k.item);
}

/** One row up (-1) or down (+1); the ends stay put. */
export function moveRow<T extends { id: string }>(
  items: T[],
  id: string,
  direction: -1 | 1
): T[] {
  const from = items.findIndex((i) => i.id === id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= items.length) return items;
  const next = [...items];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

/** Rows that cannot be saved yet: a corrective action needs a location (Jim
 * 10/30 [D-16], the erosion contractors bill by it). */
export function rowsWithoutLocation<T extends Located>(items: T[]): T[] {
  return items.filter((i) => !(i.location ?? '').trim());
}
